// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { getSupportedTraits } from '@tectonics/prs/feature';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';
import { intersect } from '@tectonics/commons/arrays';
import lookAndFeel from '@tectonics/look-and-feel/shopify';
import { useOpenedIncomingPayment, getSafeCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment';
import { getCreditCardCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment/checkout-payment-options';

import amexImageSrc from './images/credit-card-amex.svg';
import carnetImageSrc from './images/credit-card-carnet.svg';
import dinersImageSrc from './images/credit-card-diners.svg';
import discoverImageSrc from './images/credit-card-discover.svg';
import eloImageSrc from './images/credit-card-elo.svg';
import hipercardImageSrc from './images/credit-card-hipercard.svg';
import mastercardImageSrc from './images/credit-card-mastercard.svg';
import visaImageSrc from './images/credit-card-visa.svg';
import cabalImageSrc from './images/credit-card-cabal.svg';
import credimasImageSrc from './images/credit-card-credimas.svg';
import naranjaImageSrc from './images/credit-card-naranja.svg';
import privateLabelImageSrc from './images/private-label.svg';
import codensaImageSrc from './images/credit-card-codensa.png';
import rupayImageSrc from './images/credit-card-rupay.svg';

type Props = {|
  brandsFeatureName: string,
|};

export function DisplayCreditCardBrands(): Node {
  return <DisplayCardBrands brandsFeatureName="display_card_accepts_brand" />;
}

export function DisplayDebitCardBrands(): Node {
  return <DisplayCardBrands brandsFeatureName="display_debit_card_accepts_brand" />;
}

function DisplayCardBrands({ brandsFeatureName }: Props): Node {
  const incomingPayment = useOpenedIncomingPayment();
  let checkoutPaymentOptions = getSafeCheckoutPaymentOptions(incomingPayment);
  let creditcardAllowedBrands;

  creditcardAllowedBrands = getCreditCardCheckoutPaymentOptions(checkoutPaymentOptions);

  const supportedDisplayCardAcceptsBrand = getSafeSupportedFeatureByName(brandsFeatureName, lookAndFeel.prsSupport);

  const supportedBrands = getSupportedTraits(supportedDisplayCardAcceptsBrand);
  const brandsToDisplay = intersect(supportedBrands, creditcardAllowedBrands);

  if (brandsToDisplay && brandsToDisplay.length) {
    return (
      <Root tabIndex="0">
        {brandsToDisplay.map(trait => (
          <CardBrandImageWrapper key={trait}>
            <CardBrandImage src={getCardBrandImageSrc(trait)} alt="card brand logo" />
          </CardBrandImageWrapper>
        ))}
      </Root>
    );
  }

  return null;
}

const Root = styled.div`
  height: 24px;
  display: flex;
  overflow: scroll hidden;
`;

const CardBrandImageWrapper = styled.div`
  flex-grow: 1;
  &:not(:first-child) {
    padding-left: 0.5em;
  }
`;

const CardBrandImage = styled.img`
  height: 100%;
  max-width: 30px;
`;

function getCardBrandImageSrc(cardBrand: string): string {
  switch (cardBrand) {
    case 'amex':
      return amexImageSrc;

    case 'carnet':
      return carnetImageSrc;

    case 'diners':
      return dinersImageSrc;

    case 'discover':
      return discoverImageSrc;

    case 'elo':
      return eloImageSrc;

    case 'hipercard':
      return hipercardImageSrc;

    case 'mastercard':
      return mastercardImageSrc;

    case 'visa':
      return visaImageSrc;

    case 'cabal':
      return cabalImageSrc;

    case 'credimas':
      return credimasImageSrc;

    case 'naranja':
      return naranjaImageSrc;

    case 'private':
      return privateLabelImageSrc;

    case 'codensa':
      return codensaImageSrc;

    case 'rupay':
      return rupayImageSrc;

    default:
      throw new Error(`Display card brand: there is no source for "${cardBrand}"`);
  }
}
